<template>
  <div ref="PictureContainer">
    <div v-show="is_show" class="file_container com_container">
      <div :id="lightgallery_id" class="lightgallery_container" v-loading="is_load_gallery"></div>
    </div>
  </div>
</template>

<script setup>
  /**
   * 图片展示组件（支持 base64 / 本地路径 / 远程文件）
   * 自动懒加载 + LightGallery + SVG 点击回调
   */
  import { ref, defineProps, watch, onMounted, onBeforeMount, onBeforeUnmount, defineEmits, } from "vue";
  import PubSub from "pubsub-js";
  import { useStore } from "@/Store/index";
  import { ElMessage } from "element-plus";
  import lightGallery from "lightgallery";

  // LightGallery 样式与插件
  import "lightgallery/css/lightgallery.css";
  import "lightgallery/css/lg-zoom.css";
  import "lightgallery/css/lg-autoplay.css";
  import "lightgallery/css/lg-fullscreen.css";
  import "lightgallery/css/lg-rotate.css";
  import lgZoom from "lightgallery/plugins/zoom";
  import lgAutoplay from "lightgallery/plugins/autoplay";
  import lgFullscreen from "lightgallery/plugins/fullscreen";
  import lgRotate from "lightgallery/plugins/rotate";

  // ======================== 基础定义 ========================
  const store = useStore();
  const props = defineProps(["node_data", "picture_files"]);
  const emit = defineEmits([]);

  const is_show = ref(false);
  const lightgallery_id = ref(null);
  const inlineGallery = ref(null);
  const is_load_gallery = ref(false);
  const current_paths = ref(null);
  const current_picture = ref({});
  const node_data_token = ref(null);

  const PictureContainer = ref(null);
  const isVisible = ref(false);

  const selectRef = ref(null);
  const observer = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      isVisible.value = true;
      initPicture(current_paths.value);
      observer.disconnect(); // 仅触发一次
    }
  }, { threshold: 0.1 });

  // ======================== 初始化与监听 ========================
  onBeforeMount(() => {
    lightgallery_id.value = `picture_${props.node_data.title}`;
  });

  onMounted(() => {
    observer.observe(PictureContainer.value);
    // 订阅节点文件事件
    node_data_token.value = PubSub.subscribe(`files_${props.node_data.title}`, (msg, data) => {
      if (data.pictureFiles) {
        is_show.value = true;
        initPicture(data.pictureFiles);
      } else {
        is_show.value = false;
      }
    });
  });

  onBeforeUnmount(() => {
    if (node_data_token.value) PubSub.unsubscribe(node_data_token.value);
    observer.disconnect();
  });

  // ======================== 图片加载入口 ========================
  function initPicture(filePaths) {
    if (!filePaths) return;
    current_paths.value = filePaths;

    // 只有进入视口后才加载
    if (!isVisible.value) return;

    const firstKey = Object.keys(filePaths)[0];
    const firstValue = filePaths[firstKey];
    handleFileType(firstValue, filePaths);
  }

  // ======================== 文件类型识别与分发 ========================
  function handleFileType(fileSource, allFiles) {
    const base64Pattern = /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,/; // 匹配 base64 格式
    const localPathPattern = /^\./;// 匹配以. 开头的路径

    if (base64Pattern.test(fileSource)) {
      // 处理 base64 格式
      handleBase64Files(allFiles);
    } else if (localPathPattern.test(fileSource)) {
      // 处理本地路径格式
      handleLocalFiles(allFiles);
    } else {
      // 处理远程文件格式
      fetchRemoteFiles(allFiles);
    }
  }

  // ======================== 统一数据标准化 ========================
  function formatFiles(fileList, formatter) {
    const files = Array.isArray(fileList) ? fileList : typeof fileList === "object" ? Object.values(fileList) : [fileList];
    const keys = typeof fileList === "object" ? Object.keys(fileList) : files.map((_, i) => i);
    return files.map((file, i) => formatter(file, keys[i]));
  }

  // ======================== 各类型文件处理 ========================
  function fetchRemoteFiles(fileData) {
    is_load_gallery.value = true;

    const result = formatFiles(fileData, (src, key) => {
      const url = process.env.VUE_APP_BASE_API + src;
      const isSvg = src.endsWith(".svg");
      return {
        src: url,
        thumb: url,
        key,
        isSvg,
        svgPath: src,
      };
    });

    renderGallery(result);
  }

  function handleBase64Files(fileData) {
    const result = formatFiles(fileData, (dataUrl, key) => ({
      src: dataUrl,
      thumb: dataUrl,
      key,
      isSvg: dataUrl.includes("image/svg+xml"),
      svgData: dataUrl.includes("image/svg+xml")
        ? decodeURIComponent(dataUrl.split(",")[1])
        : null,
    }));

    renderGallery(result);
  }

  function handleLocalFiles(fileData) {
    const result = formatFiles(fileData, (path, key) => ({
      src: path,
      thumb: path,
      key,
      isSvg: path.endsWith(".svg"),
    }));

    renderGallery(result);
  }

  // ======================== 画廊加载 ========================
  function renderGallery(files) {


    if (inlineGallery.value) {
      /* try {
        inlineGallery.value.destroy(true); // 安全销毁，不触发 DOM 删除
      } catch (e) {
        console.warn('销毁 gallery 出错:', e);
      } */
      inlineGallery.value = null;
    }





    // 2️ 清空旧容器内容
    const container = document.getElementById(lightgallery_id.value);
    if (container) {
      container.innerHTML = "";
      container.replaceWith(container.cloneNode(false)); // 清理旧事件
    }
    const newContainer = document.getElementById(lightgallery_id.value);
    // 3️ 初始化新的 gallery 实例
    const gallery = lightGallery(newContainer, {
      container: newContainer,
      dynamic: true,
      hash: false,
      closable: false,
      showMaximizeIcon: true,
      appendSubHtmlTo: ".lg-item",
      slideDelay: 400,
      plugins: [lgZoom, lgAutoplay, lgFullscreen, lgRotate],
      dynamicEl: files,
      thumbWidth: 60,
      thumbHeight: "40px",
      thumbMargin: 4,
      preload: 0,
      keyPress: false,
    });
    inlineGallery.value = gallery;
    // 4️ 绑定SVG点击事件
    newContainer.addEventListener("click", (e) => handleSvgClick(e, files));
    // 绑定切换事件
    newContainer.addEventListener("lgBeforeSlide", (e) => {
      if (selectRef.value) selectRef.value.selectedIndex = e.detail.index;
    });

    // 5️ 打开 gallery 并创建下拉框
    setTimeout(() => {
      gallery.openGallery();

      // ✅ 创建下拉框前先确认 toolbar 已加载
      const toolbarReady = setInterval(() => {
        if (inlineGallery.value?.$toolbar?.get?.()) {
          clearInterval(toolbarReady);
          createGallerySelector(files);
          is_load_gallery.value = false;
        }
      }, 50);
    }, 200);
  }

  // ======================== SVG 点击事件 ========================
  function cleanSvg(text) {
    return text
      .replace(/^\s*<\?xml.*\?>/, "")
      .replace(/<!DOCTYPE svg[^>]*>/i, "")
      .trim();
  }

  async function handleSvgClick(event, files) {
    const target = event.target;
    if (!(target.tagName === "IMG" && target.classList.contains("lg-object"))) return;
    if (!target.src.endsWith(".svg")) return;
    if (window.location.protocol === "file:") {
      return console.error("本地模式下无法加载 SVG 文件。");
    }
    const index = target.getAttribute("data-index");
    const item = files[index];
    const url = process.env.VUE_APP_BASE_API + item.svgPath;
    try {
      const response = await fetch(url, {
        headers: { Ticket: store.ticket || null },
      });
      const blob = await response.blob();
      const data = blob.type === "image/svg+xml" ? cleanSvg(await blob.text()) : URL.createObjectURL(blob);
      current_picture.value = {
        data,
        path: item.svgPath,
        title: props.node_data.title,
      };
      PubSub.publish("svg_data", current_picture.value);
    } catch (e) {
      console.error("加载 SVG 出错：", e);
      ElMessage.error("加载 SVG 出错");
    }
  }

  // ======================== 下拉选择器 ========================
  function createGallerySelector(files) {
    const toolbar = inlineGallery.value?.$toolbar?.get?.();
    if (!toolbar) return;
    // 先移除旧的下拉框
    const oldSelectContainer = toolbar.querySelector(".select_container");
    if (oldSelectContainer) oldSelectContainer.remove();
    // 创建新容器
    const selectContainer = document.createElement("div");
    selectContainer.className = "select_container";
    const select = document.createElement("select");
    select.className = "lg-select";
    selectRef.value = select;
    files.forEach((item, index) => {
      const option = document.createElement("option");
      option.value = index;
      option.textContent = item.key || `图片 ${index + 1}`;
      select.appendChild(option);
    });
    // 事件绑定时先解绑旧事件
    select.addEventListener("change", (e) => {
      inlineGallery.value?.slide(Number(e.target.value));
    });
    selectContainer.appendChild(select);
    toolbar.appendChild(selectContainer);
  }

</script>

<style scoped>
  .com_container {
    margin-top: 15px;
  }

  .lightgallery_container {
    width: 100%;
    height: 600px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  }

  :deep(.lg-img-wrap) {
    overflow: hidden;
  }
</style>

<style>
  .lg-outer .lg-inner {
    background-color: #fff;
  }
</style>